/**
 * 画布视窗交互 hook
 * - 滚轮：默认平移画布（deltaY 垂直、deltaX 水平，如 Magic Mouse 侧滑 / 触控板双指）
 * - ⌘（Mac）/ Ctrl（Win）+ 滚轮：以指针为中心缩放
 * - Space 或「小手」工具 + 拖拽平移
 */

import { useRef, useCallback, useLayoutEffect, useState } from 'react';
import { WORKSPACE_ZOOM_MIN, WORKSPACE_ZOOM_MAX } from '@/components/canvas/shared/constants/legacyConfig';

function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent || navigator.platform || '');
}

/** Mac：Command；Windows/Linux：Ctrl */
function isZoomWheelModifier(e: WheelEvent): boolean {
  return isMacPlatform() ? e.metaKey : e.ctrlKey;
}

interface UseCanvasViewportOptions {
  workspaceRef: React.RefObject<HTMLDivElement | null>;
  zoom: number;
  panX: number;
  panY: number;
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;
  /** 为 true 时禁用画布滚轮缩放（例如图片进入「移动/缩放」编辑态时，滚轮留给图片缩放） */
  wheelZoomDisabled?: boolean;
  /** 演示全屏时禁用滚轮平移（避免触控板/妙控鼠标滑动翻页视口） */
  wheelPanDisabled?: boolean;
  /** 空格按住时的临时平移模式 */
  isSpacePressed: boolean;
  /** 与按住空格等效：左键拖拽平移画布（工具栏小手模式） */
  handPanToolActive?: boolean;
}

export function useCanvasViewport({
  workspaceRef,
  zoom,
  panX,
  panY,
  setZoom,
  setPan,
  wheelZoomDisabled = false,
  wheelPanDisabled = false,
  isSpacePressed,
  handPanToolActive = false,
}: UseCanvasViewportOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const dragStartPanRef = useRef({ x: 0, y: 0 });

  const stateRef = useRef({ zoom, panX, panY, setZoom, setPan, wheelZoomDisabled, wheelPanDisabled });
  stateRef.current = { zoom, panX, panY, setZoom, setPan, wheelZoomDisabled, wheelPanDisabled };

  useLayoutEffect(() => {
    const el = workspaceRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      const { wheelZoomDisabled: zoomOff, wheelPanDisabled: panOff } = stateRef.current;
      if (zoomOff && panOff) return;

      const { zoom: z, panX: px, panY: py, setZoom: sz, setPan: sp } = stateRef.current;

      if (!isZoomWheelModifier(e)) {
        if (panOff) return;
        e.preventDefault();
        e.stopPropagation();
        // 与「自然滚动」一致：delta 为正时视口向该方向看过去 → 画布反向平移
        sp(px - e.deltaX, py - e.deltaY);
        return;
      }

      if (zoomOff) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const newZoom = Math.max(WORKSPACE_ZOOM_MIN, Math.min(WORKSPACE_ZOOM_MAX, z + (e.deltaY > 0 ? -0.005 : 0.005)));
      const worldX = (mouseX - px) / z;
      const worldY = (mouseY - py) / z;
      sz(newZoom);
      sp(mouseX - worldX * newZoom, mouseY - worldY * newZoom);
    };
    el.addEventListener('wheel', handler, { passive: false, capture: true });
    return () => el.removeEventListener('wheel', handler, { capture: true });
  }, [workspaceRef]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((isSpacePressed || handPanToolActive) && e.button === 0) {
        e.preventDefault();
        setIsDragging(true);
        dragStartRef.current = { x: e.clientX, y: e.clientY };
        dragStartPanRef.current = { x: panX, y: panY };
      }
    },
    [isSpacePressed, handPanToolActive, panX, panY]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && (isSpacePressed || handPanToolActive)) {
        e.preventDefault();
        const dx = e.clientX - dragStartRef.current.x;
        const dy = e.clientY - dragStartRef.current.y;
        setPan(dragStartPanRef.current.x + dx, dragStartPanRef.current.y + dy);
      }
    },
    [isDragging, isSpacePressed, handPanToolActive, setPan]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (e.button === 0 && isDragging) {
        e.preventDefault();
        setIsDragging(false);
      }
    },
    [isDragging]
  );

  const panActive = isSpacePressed || handPanToolActive;
  const panCursor = isDragging && panActive ? 'grabbing' : panActive ? 'grab' : undefined;

  return {
    isDragging,
    setIsDragging,
    panCursor,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
}
